import { useEffect } from "react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../api";
import Sidebar from "../../components/Sidebar";
import Breadcrumbs from "../../components/Breadcrumbs";
import TextEditor from "../../components/TextEditor";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import toast, { Toaster } from "react-hot-toast";
import SelectInput from "../../components/SelectInput";

export default function ReportPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [patient, setPatient] = useState();
    const [loading, setLoading] = useState(false);
    const [loadingPrint, setLoadingPrint] = useState(false);
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const { idPatient } = useParams();
    const [reports, setReports] = useState([]);
    const [printInsurance, setPrintInsurance] = useState("");
    // eslint-disable-next-line no-unused-vars
    const [userId, setUserId] = useState(
        JSON.parse(localStorage.getItem("BDOuserData"))?.id
    );
    const [reportToDelete, setReportToDelete] = useState(null);
    const [reportForm, setReportForm] = useState({
        patient: idPatient,
        title: "",
        rightEye: "",
        leftEye: "",
        conclusion: "",
    });
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    const notify = (message, type = "success") => {
        toast(message, {
            style: {
                background: type === "success" ? "#4caf50" : "#f44336",
                color: "#fff",
            },
        });
    };
    const pages = [
        { name: "Pacientes", href: "/patients", current: false },
        {
            name: `${patient?.name}`,
            href: `/patients/${idPatient}`,
            current: false,
        },
        { name: "Informes", href: `#`, current: true },
    ];

    useEffect(() => {
        api.patients
            .getOne(idPatient)
            .then((response) => {
                setPatient({
                    ...response,
                    name: response.firstName + " " + response.lastName,
                });
            })
            .catch((error) => console.log(error));
    }, [idPatient]);

    useEffect(() => {
        setLoading(true);
        api.reports
            .getMulti("-created_at", null, null, null, { patient: idPatient })
            .then((data) => {
                const items = data?.results ?? data ?? [];
                setReports(items);
            })
            .catch(() => notify("Error al cargar los informes", "error"))
            .finally(() => setLoading(false));
    }, [idPatient]);

    const handleOpenReportModal = (data) => {
        if (data) {
            // Modo edición: cargar sólo los campos necesarios y conservar el doctor original
            setReportForm({
                id: data.id,
                patient: data.patient,
                title: data.title || "",
                rightEye: data.rightEye || "",
                leftEye: data.leftEye || "",
                conclusion: data.conclusion || "",
                doctor: data.doctor?.id,
            });
        } else {
            setReportForm({
                patient: idPatient,
                title: "",
                rightEye: "",
                leftEye: "",
                conclusion: "",
            });
        }
        setFormErrors({});
        setIsReportModalOpen(true);
    };

    const handleCloseReportModal = () => {
        setFormErrors({});
        setIsReportModalOpen(false);
    };

    const handleChangeReportForm = (event) => {
        const { name, value } = event.target;
        setReportForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleChangeReportEditor = (name, content) => {
        setReportForm((prev) => ({
            ...prev,
            [name]: content,
        }));
    };

    const handleSubmitReport = async (event) => {
        event.preventDefault();

        const title = (reportForm.title || "").trim();
        const conclusionText = (reportForm.conclusion || "")
            .replace(/<[^>]*>/g, "")
            .trim();

        const errors = {};
        if (!title) errors.title = "El título es obligatorio.";
        if (!conclusionText) errors.conclusion = "La conclusión es obligatoria.";

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setFormErrors({});

        setLoading(true);

        try {
            const payload = {
                ...reportForm,
                doctor: reportForm.doctor ?? userId,
            };
            console.log(payload);
            const isEditing = !!reportForm.id;
            if (isEditing) {
                await api.reports.update(reportForm.id, payload);
            } else {
                await api.reports.create(payload);
            }

            notify(isEditing ? "Informe actualizado correctamente" : "Informe creado correctamente");

            const data = await api.reports.getMulti(null, null, null, null, {
                patient: idPatient,
            });
            const items = data?.results ?? data ?? [];
            setReports(items);

            setReportForm({
                patient: idPatient,
                title: "",
                rightEye: "",
                leftEye: "",
                conclusion: "",
            });
            setIsReportModalOpen(false);
        } catch (error) {
            notify("Error al guardar el informe", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDeleteModal = (report) => {
        setReportToDelete(report);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setReportToDelete(null);
        setIsDeleteModalOpen(false);
    };

    const handleConfirmDeleteReport = async () => {
        if (!reportToDelete) return;
        setLoading(true);
        try {
            await api.reports.delete(reportToDelete.id);
            const data = await api.reports.getMulti(null, null, null, null, {
                patient: idPatient,
            });
            const items = data?.results ?? data ?? [];
            setReports(items);
            setReportToDelete(null);
            setIsDeleteModalOpen(false);
            notify("Informe eliminado correctamente");
        } catch (error) {
            notify("Error al eliminar el informe", "error");
        } finally {
            setLoading(false);
        }
    };

    const handlePrintReport = async (report) => {
        if (!report?.id) return;

        const title = (report.title || "").trim();
        const conclusionText = (report.conclusion || "")
            .replace(/<[^>]*>/g, "")
            .trim();

        if (!title || !conclusionText) {
            notify("No se puede imprimir: el título y la conclusión son obligatorios.", "error");
            return;
        }

        setLoadingPrint(true);
        try {
            const data = await api.reportsExport.export(report.id, printInsurance);
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${report.title || "informe"}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            notify("Error al generar el PDF", "error");
        } finally {
            setLoadingPrint(false);
        }
    };

    return (
        <div className="bg-gray-100 dark:bg-gray-800 h-screen">
            <Sidebar
                currentScreen={"Pacientes"}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
            />
            <div className="md:pl-64 flex flex-col bg-gray-100 dark:bg-gray-800 flex-1">
                <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-100 dark:bg-gray-800">
                    <button
                        type="button"
                        className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                        onClick={() => setSidebarOpen(true)}
                    >
                        <span className="sr-only">Open sidebar</span>
                        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                    </button>
                </div>
                <div className="flex flex-col flex-1 pb-4 ml-6 mt-6">
                    <Breadcrumbs pages={pages} />
                    <div className="ml-4"></div>
                </div>
                <main className="flex-1 bg-gray-100 dark:bg-gray-800 px-6 pb-8 mt-12">
                    <ReportsTable
                        reports={reports}
                        onOpenReportModal={handleOpenReportModal}
                        loading={loading}
                        onDeleteReport={handleOpenDeleteModal}
                        onPrintReport={handlePrintReport}
                        loadingPrint={loadingPrint}
                        printInsurance={printInsurance}
                        setPrintInsurance={setPrintInsurance}
                        patient={patient}
                    />

                    <ReportModal
                        isOpen={isReportModalOpen}
                        onClose={handleCloseReportModal}
                        onChange={handleChangeReportForm}
                        onEditorChange={handleChangeReportEditor}
                        onSubmit={handleSubmitReport}
                        values={reportForm}
                        loading={loading}
                        errors={formErrors}
                    />

                    {isDeleteModalOpen && reportToDelete && (
                        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40">
                            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full mx-4">
                                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100">
                                        Eliminar informe
                                    </h3>
                                </div>
                                <div className="px-6 py-4 space-y-3">
                                    <p className="text-sm text-gray-700 dark:text-gray-300">
                                        ¿Estás seguro que deseas eliminar este
                                        informe?
                                    </p>
                                    <p className="text-sm text-gray-900 dark:text-gray-100 font-medium truncate">
                                        {reportToDelete.title}
                                    </p>
                                </div>
                                <div className="px-6 py-4 flex justify-end gap-x-3 border-t border-gray-200 dark:border-gray-800">
                                    <button
                                        type="button"
                                        onClick={handleCloseDeleteModal}
                                        className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleConfirmDeleteReport}
                                        className="inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:cursor-wait"
                                        disabled={loading}
                                    >
                                        {loading ? "Eliminando..." : "Eliminar"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>
            <Toaster />
        </div>
    );
}

function ReportModal({
    isOpen,
    onClose,
    onChange,
    onEditorChange,
    onSubmit,
    values,
    loading,
    errors = {},
}) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-999 flex items-center justify-center bg-black/40">
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full mx-4">
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-6 py-4">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100">
                        Generar nuevo informe
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        <span className="sr-only">Cerrar</span>
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>
                <form
                    onSubmit={onSubmit}
                    className="px-6 py-6 space-y-4 max-h-[80vh] overflow-y-auto"
                >
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                            Título
                            <span className="font-medium text-[#F51515] ml-2">*</span>
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={values.title}
                            onChange={onChange}
                            className={`mt-1 block w-full rounded-md shadow-sm focus:border-primary focus:ring-primary dark:bg-gray-900 dark:text-gray-100 ${errors.title ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-700'}`}                        />
                        {errors.title && (
                            <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                        )}
                    </div>
                    {/* <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <TextEditor
                            name="rightEye"
                            label="Ojo derecho"
                            value={values.rightEye}
                            handleChange={onEditorChange}
                            height={160}
                        />
                        <TextEditor
                            name="leftEye"
                            label="Ojo izquierdo"
                            value={values.leftEye}
                            handleChange={onEditorChange}
                            height={160}
                        />
                    </div> */}
                    <div>
                        <TextEditor
                            name="conclusion"
                            value={values.conclusion}
                            handleChange={onEditorChange}
                            height={200}
                            containerClassName="pb-10 pt-5"
                        />
                        {errors.conclusion && (
                            <p className="mt-1 text-sm text-red-500">{errors.conclusion}</p>
                        )}
                    </div>
                    <div className="mt-4 flex justify-end gap-x-3 border-t border-gray-200 dark:border-gray-800 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="inline-flex justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-wait"
                            disabled={loading}
                        >
                            {loading ? (
                                <span>
                                    <div className="flex items-center justify-center space-x-2">
                                        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                </span>
                            ) : (
                                "Guardar informe"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function ReportsTable({
    reports,
    onOpenReportModal,
    loading,
    onDeleteReport,
    onPrintReport,
    loadingPrint,
    setPrintInsurance,
    printInsurance,
    patient,
}) {
    const formatDate = (value) => {
        if (!value) return "";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "";
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const insuranceOptions = [
        <option key="empty" value="">
            Sin seguro
        </option>,
        ...(patient?.insuranceNames?.map((name, index) => (
            <option key={patient.insurances[index]} value={patient.insurances[index]}>
                {name}
            </option>
        )) || [])
    ];

    return (
        <div className="bg-white dark:bg-gray-900 shadow sm:rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Historial de informes
                </h2>
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 text-black dark:text-white">
                        <SelectInput
                            name="insurance"
                            className = {"shadow-sm w-[200px] mt-1 mx-auto pr-8 border border-gray-300 block sm:text-sm rounded-md input-base"}
                            options={insuranceOptions}
                            value={printInsurance}
                            handleChange={(name, value) => setPrintInsurance(value)}
                            placeholder
                            placeholderText="Seleccionar seguro"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => onOpenReportModal()}
                        className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    >
                        Generar informe
                    </button>
                </div>
            </div>

            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900/50">
                            <tr>
                                <th
                                    scope="col"
                                    className="w-px px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                >
                                    Fecha
                                </th>
                                <th
                                    scope="col"
                                    className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                >
                                    Creado por
                                </th>
                                <th
                                    scope="col"
                                    className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                >
                                    Título
                                </th>
                                <th
                                    scope="col"
                                    className="w-px px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                                >
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
                            {loading ? (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                                    >
                                        <div className="flex items-center justify-center space-x-2">
                                            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="py-2 text-center text-gray-500 dark:text-gray-400"
                                    >
                                        No se encontraron informes
                                    </td>
                                </tr>
                            ) : (
                                reports.map((report) => (
                                    <tr key={report.id}>
                                        <td className="w-px whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-gray-100">
                                            {formatDate(report.createdAt)}
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                            {`${
                                                report.doctor?.firstName || ""
                                            } ${
                                                report.doctor?.lastName || ""
                                            }`.trim() || "-"}
                                        </td>
                                        <td className="w-full px-3 py-4 text-sm text-gray-900 dark:text-gray-100">
                                            <span
                                                className="block overflow-hidden text-ellipsis whitespace-nowrap md:whitespace-normal md:truncate"
                                                title={report.title}
                                            >
                                                {report.title}
                                            </span>
                                        </td>
                                        <td className="w-px px-3 py-4 text-sm space-x-2">
                                            <div className="flex flex-col md:flex-row gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        onOpenReportModal(
                                                            report
                                                        )
                                                    }
                                                    className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1  font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                                                >
                                                    Editar
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        onPrintReport(report)
                                                    }
                                                    className="inline-flex items-center rounded-md border border-gray-200 bg-white px-3 py-1  font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 disabled:cursor-wait disabled:bg-gray-200 disabled:dark:bg-gray-800"
                                                    disabled={loadingPrint}
                                                >
                                                    Imprimir
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        onDeleteReport(report)
                                                    }
                                                    className="inline-flex items-center rounded-md border border-red-100 bg-red-50 px-3 py-1  font-semibold text-red-700 shadow-sm hover:bg-red-100 dark:border-red-900 dark:bg-red-950 dark:text-red-200 dark:hover:bg-red-900"
                                                >
                                                    Eliminar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
